import { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';

import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import { Select, MenuItem, InputLabel, Typography } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { ApplicationStage } from 'src/services/applications/applications.types';
import { OrganizationReasonType } from 'src/services/organisation/organisation.types';
import { getUpdateStageErrorMessage } from 'src/services/applications/applications.utils';
import { useGetOrganizationReasonsQuery } from 'src/services/organisation/organisation.service';
import { useProgressCandidateApplicationMutation } from 'src/services/applications/applications.service';

import { ConfirmDialog } from 'src/components/custom-dialog';

type Props = {
  open: boolean;
  applicationId: string | null;
  /** The user backed out without withdrawing. */
  onClose: VoidFunction;
  onWithdrawn?: VoidFunction;
};

/**
 * Moves an application the organisation manages to Withdrawn, with one of its own withdrawal
 * reasons (JT1-1800). The application stays on the board in the Withdrawn column.
 */
export default function ApplicationWithdrawDialog({ open, applicationId, onClose, onWithdrawn }: Props) {
  const { t } = useTranslate();

  const { organization } = useAuthContext();

  const [reasonId, setReasonId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { currentData: reasons, isFetching: isLoadingReasons } = useGetOrganizationReasonsQuery(
    { org_id: organization.id, type: OrganizationReasonType.ApplicationWithdrawal },
    { skip: !open }
  );

  const [progressApplication, { isLoading: isWithdrawing }] = useProgressCandidateApplicationMutation();

  useEffect(() => {
    if (open) {
      setReasonId('');
      setErrorMessage('');
    }
  }, [open]);

  const handleWithdraw = async () => {
    setErrorMessage('');

    if (!reasonId) {
      setErrorMessage(t('applications.withdraw.reason_required'));
      return;
    }

    try {
      await progressApplication({
        applicationId: applicationId as string,
        data: {
          stage: ApplicationStage.WITHDRAWN,
          withdrawal_reason_id: reasonId,
        },
      }).unwrap();

      enqueueSnackbar(t('applications.withdraw.success'));
      onWithdrawn?.();
    } catch (e) {
      console.error(e);

      enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={t('applications.withdraw.team_title')}
      content={
        <Stack spacing={2} direction="column">
          <Typography variant="body2">
            {t('applications.withdraw.team_confirm')}
          </Typography>

          {errorMessage && (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          )}

          <Stack>
            <InputLabel required>{t('applications.withdraw.reason_label')}</InputLabel>
            <Select
              required
              value={reasonId}
              disabled={isLoadingReasons}
              onChange={(e) => setReasonId(e.target.value)}
              inputProps={{ 'aria-label': t('applications.withdraw.reason_label') }}
            >
              {(reasons ?? []).map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      }
      action={
        <LoadingButton variant="contained" color="error" loading={isWithdrawing} onClick={handleWithdraw}>
          {t('applications.withdraw.action')}
        </LoadingButton>
      }
    />
  );
}
